import * as React from 'react';
import { Bar } from 'react-chartjs-2';
import { ApiInstance } from '../../api/api';
import { utils_instance } from '../../utils/Utils';
import s from './analytic.module.css';
import { ChartOptions } from 'chart.js';
import {
  MonthlyAnalyticDataType,
  DataSetType,
  ActionType,
  RoleType,
} from '../../types/analyticTypes';
import TablePlaceholder from '../placeholders/TablePlaceholder';
import BuildingProcessingError from '../placeholders/BuildingProcessingError';

const MonthlyAnalyticDataTypeInitState = {
  month: '',
  action_counts: [
    {
      action: '',
      action_count: 0,
    },
  ],
};

type AnnualActionAnalyticProps = {
  actions: ActionType[];
  role: RoleType;
  users: string[];
};

const AnnualActionAnalytic: React.FC<AnnualActionAnalyticProps> = (props) => {
  const { actions, role, users } = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [apiErrorFlag, setApiErrorFlag] = React.useState<Boolean>(false);
  const [labels, setLabels] = React.useState<string[]>([]);
  const [datasets, setDatasets] = React.useState<DataSetType>();
  const [actionData, setActionData] = React.useState<MonthlyAnalyticDataType[]>([]);
  const [actionDataItem, setActionDataItem] = React.useState<MonthlyAnalyticDataType>(
    MonthlyAnalyticDataTypeInitState
  );
  const [isMouseOnBar, setIsMouseOnBar] = React.useState(false);

  const getAnnualAnalytic = async () => {
    try {
      setIsLoading(true);
      setApiErrorFlag(false);

      const annualAnalytic = await ApiInstance.getAnnualAnalytic(actions, role, users);
      setLabels(annualAnalytic.reverse().map((item: MonthlyAnalyticDataType) => item.month));
      setActionData(annualAnalytic);
      const actionCounts: {
        [action: string]: number[];
      } = {};
      annualAnalytic.forEach((monthData: MonthlyAnalyticDataType) => {
        monthData.action_counts.forEach((actionData) => {
          const action = actionData.action;
          const count = actionData.action_count;

          if (!actionCounts[action]) {
            actionCounts[action] = [];
          }

          actionCounts[action].push(count);
        });
      });
      setDatasets(actionCounts);
    } catch (e: any) {
      setApiErrorFlag(true);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    getAnnualAnalytic();
  }, [actions, role, users]);

  const onBarHover = (event: any, activeElements: any) => {
    if (activeElements && activeElements.length > 0) {
      const hoveredIndex = activeElements[0].index;
      setActionDataItem(actionData[hoveredIndex]);
      setIsMouseOnBar(true);
    } else {
      setIsMouseOnBar(false);
    }
  };

  const options: ChartOptions<'bar'> = {
    aspectRatio: 3,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    onHover: onBarHover,
    plugins: {
      legend: {
        position: 'bottom',
        display: false,
      },
    },
  };

  const data = {
    labels,
    datasets: datasets
      ? Object.entries(datasets).map(([label, data], index) => ({
        label: label,
        data: data,
        backgroundColor: utils_instance.colorList[index],
      }))
      : [],
  };

  const legendIfMouseOver = datasets ? (
    Object.keys(datasets).map((item, index) => {
      const actionCount = datasets[item].reduce((sum, value) => sum + value, 0);
      const backgroundColor = utils_instance.colorList[index % utils_instance.colorList.length];
      return (
        actionCount !== 0 && (
          <div key={index} className={s.legend_line}>
            <div style={{ backgroundColor: backgroundColor }} className={s.legend_color} />
            <p style={{ marginTop: '0px' }}>
              {item} - {actionCount}
            </p>
          </div>
        )
      );
    })
  ) : (
    <></>
  );

  const legend = actionDataItem.action_counts.map((item, index) => {
    const backgroundColor = utils_instance.colorList[index % utils_instance.colorList.length];
    return (
      item.action_count !== 0 && (
        <div key={index} className={s.legend_line}>
          <div style={{ backgroundColor: backgroundColor }} className={s.legend_color} />
          <p style={{ marginTop: '0px' }}>
            {item.action} - {item.action_count}
          </p>
        </div>
      )
    );
  });

  const totalAmountYearly = () => {
    let totalAmount = 0;

    for (const action in datasets) {
      const actionCount = datasets[action].reduce((sum, value) => sum + value, 0);
      totalAmount += actionCount;
    }
    return <p>Total action amount - {totalAmount}</p>;
  };

  const totalAmountMonthly = () => {
    let totalAmount = 0;

    for (const actionCount of actionDataItem.action_counts) {
      totalAmount += actionCount.action_count;
    }
    return <p>Total action amount - {totalAmount}</p>;
  };

  return (
    <div className={`${s.content} ${s.paper_style}`}>
      <div>
        <div className={s.fields}>
          <div className={s.card_header}>Users' actions by type</div>
          {isLoading ? (
            <TablePlaceholder rows={9} columns={['analytics']} showHeader={false} />
          ) : (
            <div>
              {apiErrorFlag ? (
                <BuildingProcessingError entity={'analytics'} />
              ) : (
                <Bar options={options} data={data} />
              )}
            </div>
          )}
        </div>
        <div>
          <p>{isMouseOnBar ? actionDataItem.month : 'During last year:'}</p>
          <div className={s.legend_container}>{isMouseOnBar ? legend : legendIfMouseOver}</div>
          <div>{isMouseOnBar ? totalAmountMonthly() : totalAmountYearly()}</div>
        </div>
      </div>
    </div>
  );
};

export default AnnualActionAnalytic;
